import { Box, TypographyStylesProvider } from "@mantine/core";
import React from "react";
import { IconBolt } from "@tabler/icons-react";
import DocsButton from "../articlesHelpers/DocsButton";
import { JsonView, allExpanded, darkStyles } from "react-json-view-lite";

export const LIGHTNING_RECEIVED_CONTENT = (
  <TypographyStylesProvider>
    <p>
      From the History menu, you can see detailed information about inbound
      lightning transactions, with this button:
    </p>
    <DocsButton
      ariaLabel="Lightning Received button"
      text="Lightning Received"
      leftSection={<IconBolt />}
    />
    <p>Here is an example of the data: </p>
    <Box mb="20px">
      <JsonView
        data={{
          id: "2c2a73cee14cfc843cb8d08db08bbd6e4eec0e7600e45867ab78ba84d6f2e8c9",
          index: 106,
          secret:
            "909a75701d1a6b2c274813f7e90f841ac43ebf5593048effa24f564944f4d36b",
          tokens: 0,
          mtokens: "0",
          payment:
            "bfe2ae0fabcb8dc99fc0bee27ea2e438689d641eb6b5c3fb02281dfcf75008ec",
          request:
            "lnbc1pnnwku7pp59s488nhpfn7gg09c6zxmpzaade8wcrnkqrj9seat0zagf4hjarysdqqcqzzsxqyjwn7rzjqg5pml4hdnm85ffw2rjhvhevdmxa7248ymueh682argnz6azny8hnapyqqqqqqqqqvqqqqlgqqqq86qq2qsp5hl32uratewxun87qhm38aghy8p5f6eq7k66u87cz9qwlea6sprkq9p4gqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqpqysgqfkf87w0qprkn6tvkwywd3d7k3z7psm7egsela6u7jwxp7r9vhg2qjhdpxf7cjzdafjxmd3p3h82t5tny8qun9u74cz9qlmqecuel2fqq934d2x",
          features: [
            {
              bit: 8,
              type: "tlv_onion",
              is_known: true,
              is_required: true,
            },
            {
              bit: 14,
              type: "payment_identifier",
              is_known: true,
              is_required: true,
            },
            {
              bit: 17,
              type: "multipath_payments_v0",
              is_known: true,
              is_required: false,
            },
            {
              bit: 25,
              type: "route_blinding",
              is_known: true,
              is_required: false,
            },
            {
              bit: 263,
              is_known: true,
              is_required: false,
            },
          ],
          payments: [],
          received: 0,
          cltv_delta: 80,
          created_at: "2024-11-15T14:33:02.000Z",
          expires_at: "2024-11-22T14:33:00.000Z",
          is_private: true,
          description: "",
          is_confirmed: false,
          received_mtokens: "0",
        }}
        shouldExpandNode={allExpanded}
        style={darkStyles}
      />
    </Box>
    <Box>
      <p>Let's look at each field in detail:</p>
      <ul>
        <li>
          <b>id:</b> See{" "}
          <a href="/docs/history#lightning-sent">
            Lightning Sent documentation
          </a>{" "}
          for details.
        </li>
        <li>
          <b>index:</b> See{" "}
          <a href="/docs/history#lightning-sent">
            Lightning Sent documentation
          </a>{" "}
          for details.
        </li>
        <li>
          <b>secret:</b> See{" "}
          <a href="/docs/history#lightning-sent">
            Lightning Sent documentation
          </a>{" "}
          for details.
        </li>
        <li>
          <b>tokens:</b> See{" "}
          <a href="/docs/history#lightning-sent">
            Lightning Sent documentation
          </a>{" "}
          for details.
        </li>
        <li>
          <b>mtokens:</b> See{" "}
          <a href="/docs/history#lightning-sent">
            Lightning Sent documentation
          </a>{" "}
          for details.
        </li>
        <li>
          <b>payment:</b> The payment hash associated with the transaction. This
          serves as an identifier for routing and settlement.
        </li>
        <li>
          <b>request:</b> See{" "}
          <a href="/docs/history#lightning-sent">
            Lightning Sent documentation
          </a>{" "}
          for details.
        </li>
        <li>
          <b>features:</b> A list of features supported by this payment request:
          <ul>
            <li>
              <b>bit:</b> The feature bit that indicates the capability or
              characteristic.
            </li>
            <li>
              <b>type:</b> The feature type, such as <i>tlv_onion</i> or{" "}
              <i>route_blinding</i>, describing the functionality.
            </li>
            <li>
              <b>is_known:</b> Whether the feature is recognized by the node.
            </li>
            <li>
              <b>is_required:</b> Whether the feature is mandatory for
              processing the payment.
            </li>
          </ul>
        </li>
        <li>
          <b>payments:</b> An array of payments received as part of this
          transaction. For this example, it’s empty, indicating no payments have
          been processed yet.
        </li>
        <li>
          <b>received:</b> The total amount received in satoshis (0 in this
          example).
        </li>
        <li>
          <b>received_mtokens:</b> The total amount received in millisatoshis (0
          in this example), providing greater precision for tracking.
        </li>
        <li>
          <b>cltv_delta:</b> The time-lock delta value, which defines the number
          of blocks for which this payment remains valid for routing.
        </li>
        <li>
          <b>created_at:</b> See{" "}
          <a href="/docs/history#lightning-sent">
            Lightning Sent documentation
          </a>{" "}
          for details.
        </li>
        <li>
          <b>expires_at:</b> A timestamp in ISO format indicating when the
          payment request expires. After this time, the request can no longer be
          used.
        </li>
        <li>
          <b>is_private:</b> Indicates whether the payment is private (e.g.,
          routed through private channels only).
        </li>
        <li>
          <b>description:</b> An optional field describing the payment. For this
          example, it’s empty.
        </li>
        <li>
          <b>is_confirmed:</b> Whether the payment has been confirmed. This
          field is `false` in this example, meaning the payment is not yet
          settled.
        </li>
      </ul>
    </Box>
  </TypographyStylesProvider>
);
